import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Toaster, toast } from "sonner";
import { TowerControl } from "lucide-react";
import "@/public-app.css";

import { getGoSummary, getAnnouncements, getWeather } from "@/lib/api";
import { useLiveStream } from "@/lib/useLiveStream";
import BottomNav from "@/components/public-app/BottomNav";
import HomeScreen from "@/components/public-app/HomeScreen";
import NavigateScreen from "@/components/public-app/NavigateScreen";
import TravelScreen from "@/components/public-app/TravelScreen";
import ReportScreen from "@/components/public-app/ReportScreen";
import AlertsScreen from "@/components/public-app/AlertsScreen";

export default function PublicApp() {
  const [tab, setTab] = React.useState("home");
  const [trip, setTrip] = React.useState(null);
  const [prefillDest, setPrefillDest] = React.useState("");
  const [nationalAlert, setNationalAlert] = React.useState(null);
  const [notifState, setNotifState] = React.useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const qc = useQueryClient();

  const notify = React.useCallback((title, body) => {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    try {
      if (navigator.serviceWorker?.ready) {
        navigator.serviceWorker.ready.then((reg) => reg.showNotification(title, { body, icon: "/icons/go-icon-192.png", badge: "/icons/go-icon-192.png" })).catch(() => new Notification(title, { body }));
      } else {
        new Notification(title, { body });
      }
    } catch { /* noop */ }
  }, []);

  const enableNotifications = async () => {
    if (typeof Notification === "undefined") return toast.error("Notifications aren't supported on this device");
    const p = await Notification.requestPermission();
    setNotifState(p);
    if (p === "granted") { toast.success("Alerts enabled"); notify("Barbados Go", "You'll be alerted the moment a storm or national alert is issued."); }
    else toast.error("Notifications blocked — enable them in your browser settings");
  };

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ["go-summary"], queryFn: getGoSummary, refetchInterval: 60000,
  });
  const { data: anns, isLoading: annsLoading } = useQuery({
    queryKey: ["announcements"], queryFn: getAnnouncements, refetchInterval: 60000,
  });
  const { data: weather } = useQuery({
    queryKey: ["weather"], queryFn: getWeather, refetchInterval: 120000,
  });
  const items = anns?.items || [];

  // Instant National Alerts + advisories on the public channel
  useLiveStream("public", (evt) => {
    if (evt.type === "national_alert" && evt.payload?.alert) {
      setNationalAlert(evt.payload.alert);
      notify(`National Alert: ${evt.payload.alert.title}`, evt.payload.alert.body);
      qc.invalidateQueries({ queryKey: ["announcements"] });
      qc.invalidateQueries({ queryKey: ["go-summary"] });
    } else if (evt.type === "announcement") {
      qc.invalidateQueries({ queryKey: ["announcements"] });
      qc.invalidateQueries({ queryKey: ["go-summary"] });
      if (evt.payload?.action === "new" && evt.payload?.announcement) {
        const a = evt.payload.announcement;
        if (a.severity !== "emergency") toast.info(a.title, { description: a.target });
      }
    }
  });

  const goNavigate = (dest = "") => { setPrefillDest(dest); setTab("navigate"); };

  // Notify when a storm escalates to a WARNING (banners handle watch level)
  const prevStorm = React.useRef();
  React.useEffect(() => {
    const s = weather?.storm_status;
    if (s && prevStorm.current && prevStorm.current !== "warning" && s === "warning") {
      notify("Storm Warning — Barbados", weather.storm_summary || "A storm warning is now in effect.");
      toast.error("Storm Warning issued", { description: weather.storm_summary });
    }
    prevStorm.current = s;
  }, [weather?.storm_status, weather?.storm_summary, notify]);

  return (
    <div className="go-app min-h-screen overflow-x-hidden bg-[#070E20]">
      <div className="relative mx-auto flex min-h-screen max-w-md flex-col bg-[#070E20] md:my-0 md:shadow-2xl">
        <main className="relative flex-1 overflow-hidden">
          {tab === "home" && (
            <HomeScreen summary={summary} weather={weather} loading={summaryLoading} trip={trip} onNavigate={goNavigate} onOpenAlerts={() => setTab("alerts")} onOpenTravel={() => setTab("travel")} />
          )}
          {tab === "navigate" && (
            <div className="absolute inset-0">
              <NavigateScreen prefillDest={prefillDest} onVerdict={setTrip} />
            </div>
          )}
          {tab === "travel" && <TravelScreen weather={weather} notify={notify} />}
          {tab === "report" && <ReportScreen onDone={() => setTab("home")} />}
          {tab === "alerts" && <AlertsScreen items={items} loading={annsLoading} notifState={notifState} onEnableNotifications={enableNotifications} />}
        </main>

        <BottomNav active={tab} onChange={setTab} alertCount={items.length} />

        {nationalAlert && (
          <div className="fixed inset-0 z-[2000] flex flex-col items-center justify-center bg-[#EF4444] px-8 text-center text-white" data-testid="national-alert-takeover">
            <div className="go-dial-pulse mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-white/20">
              <TowerControl size={52} strokeWidth={2.2} />
            </div>
            <div className="mb-2 text-sm font-bold uppercase tracking-[0.2em]">National Alert</div>
            <h1 className="font-head text-3xl font-extrabold leading-tight">{nationalAlert.title}</h1>
            <p className="mt-4 max-w-sm text-base leading-relaxed text-white/90">{nationalAlert.body}</p>
            <div className="mt-3 text-sm text-white/70">{nationalAlert.issued_by} · {nationalAlert.target}</div>
            <button onClick={() => { setNationalAlert(null); setTab("alerts"); }}
              className="mt-10 rounded-full bg-white px-10 py-3.5 text-base font-bold text-[#EF4444] transition-transform active:scale-95"
              data-testid="national-alert-ack">
              Acknowledge
            </button>
          </div>
        )}
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}
